import { useState } from 'react'
import SuggestionCardList from './SuggestionCardList'
import ScoreBadge from './ScoreBadge'
import StyleField from './StyleField'
import ShowcaseGallery from './ShowcaseGallery'
import ExtensionCallout from './ExtensionCallout'
import Logo, { LogoMark } from './Logo'
import type { PromptAnalysis, SuggestionCard } from '@/lib/api'

// Living catalog of the UI building blocks Promptioso currently uses. It has two halves:
//   1. Primitives  — the raw Tailwind class patterns reused across pages (buttons,
//                    inputs, cards, badges, banners, the color scale).
//   2. Components   — the real React components in app/javascript/components, with what
//                    they do and where they're used so new features reuse them.
//
// Keep this in sync: when you add or change a frontend component, reflect it here.
// See the `components-library` skill.

// ---- small layout helpers -------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-12 first:mt-0">
      <h2 className="text-lg font-bold tracking-tight">{title}</h2>
      <div className="mt-4 space-y-6">{children}</div>
    </section>
  )
}

// One catalog entry: a name, a one-line description, the live example, and the snippet
// that produced it (so it can be copy-pasted into a new feature).
function Item({
  name,
  description,
  snippet,
  children,
}: {
  name: string
  description: string
  snippet?: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-gray-200 p-5 dark:border-gray-800">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-mono text-sm font-semibold text-primary-700 dark:text-primary-300">{name}</h3>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">{children}</div>
      {snippet && (
        <pre className="mt-4 overflow-x-auto rounded-lg bg-gray-50 p-3 text-xs leading-relaxed text-gray-600 dark:bg-gray-950 dark:text-gray-400">
          {snippet}
        </pre>
      )}
    </div>
  )
}

// A row of color swatches. Class names are passed as literals so Tailwind's content
// scanner keeps them (dynamically-built class strings get purged from the build).
function Swatches({ swatches }: { swatches: [string, string][] }) {
  return (
    <div className="flex flex-wrap gap-1">
      {swatches.map(([label, cls]) => (
        <div key={label} className="text-center">
          <div className={`h-12 w-12 rounded-md ${cls}`} />
          <div className="mt-1 text-[10px] text-gray-400">{label}</div>
        </div>
      ))}
    </div>
  )
}

// ---- sample data for live component previews -------------------------------

const SAMPLE_CARDS: SuggestionCard[] = [
  {
    id: 'demo-1',
    kind: 'vague_word',
    dimension: 'mood',
    original: 'nice',
    suggestions: ['warm', 'uplifting', 'bittersweet'],
    reason: 'Pick a concrete mood word so Suno commits to a feeling.',
  },
  {
    id: 'demo-2',
    kind: 'missing_dimension',
    dimension: 'tempo',
    original: null,
    suggestions: ['90 BPM', 'driving 128 BPM'],
    reason: 'No tempo specified — add one to control the energy.',
  },
]

const SAMPLE_ANALYSIS: PromptAnalysis = {
  score: 48,
  coherence: 100,
  ready: false,
  readback: 'Suno will hear a synth-pop track — nice, 90s. Still open: percussion, tempo, vocals.',
  requiredDimensions: ['genre', 'mood', 'instrumentation', 'percussion', 'tempo', 'vocals', 'production'],
  covered: 4,
  total: 7,
  label: 'Getting there',
  hints: SAMPLE_CARDS.map((c) => ({
    id: c.id,
    kind: c.kind,
    dimension: c.dimension,
    term: c.original,
    start: null,
    length: null,
    suggestions: c.suggestions,
    message: c.reason,
  })),
}

// ---------------------------------------------------------------------------

export default function ComponentsLibrary() {
  const [text, setText] = useState('epic love song')

  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-500">
        The design system and React components currently in use. Reuse these when building new
        features instead of inventing new ones — and add anything new here.
      </p>

      <Section title="Brand">
        <Item
          name="<Logo />"
          description="brand lockup — gradient soundwave + sparkle mark, white→cyan wordmark"
          snippet={`<Logo size="lg" showTagline />   // header lockup\n<Logo mark size="sm" />          // mark only`}
        >
          <Logo size="lg" showTagline />
        </Item>
        <Item
          name="<LogoMark />"
          description="just the mark — favicon (public/icon.svg), avatars, compact spots"
        >
          <div className="flex items-end gap-4">
            <LogoMark size="sm" />
            <LogoMark size="md" />
            <LogoMark size="lg" />
          </div>
        </Item>
      </Section>

      <Section title="Color">
        <Item name="primary" description="Electric Cyan / Cyber Blue — tailwind.config.js theme.colors.primary">
          <Swatches
            swatches={[
              ['50', 'bg-primary-50'],
              ['100', 'bg-primary-100'],
              ['200', 'bg-primary-200'],
              ['300', 'bg-primary-300'],
              ['400', 'bg-primary-400'],
              ['500', 'bg-primary-500'],
              ['600', 'bg-primary-600'],
              ['700', 'bg-primary-700'],
              ['800', 'bg-primary-800'],
              ['900', 'bg-primary-900'],
              ['950', 'bg-primary-950'],
            ]}
          />
        </Item>
        <Item name="grays" description="neutral scale (gray-*) — text, borders, surfaces">
          <Swatches
            swatches={[
              ['100', 'bg-gray-100'],
              ['200', 'bg-gray-200'],
              ['300', 'bg-gray-300'],
              ['400', 'bg-gray-400'],
              ['500', 'bg-gray-500'],
              ['600', 'bg-gray-600'],
              ['700', 'bg-gray-700'],
              ['800', 'bg-gray-800'],
              ['900', 'bg-gray-900'],
            ]}
          />
        </Item>
      </Section>

      <Section title="Buttons">
        <Item
          name="Primary button"
          description="main calls to action — Matte Cyan Pill"
          snippet={`// .btn-primary is a component class in styles/index.css (Matte Cyan Pill:\n// flat cyan→teal gradient, rounded-full, no gloss, soft glow + lift on hover).\nclassName="btn-primary"`}
        >
          <button className="btn-primary">Improve (3 credits)</button>
          <button disabled className="btn-primary">
            Disabled
          </button>
        </Item>
        <Item
          name="Secondary button"
          description="quiet counterpart to the primary CTA — matte ghost pill"
          snippet={`// .btn-secondary is a component class in styles/index.css (matte ghost pill:\n// transparent fill, hairline ring, brightens on hover). Pairs with .btn-primary.\nclassName="btn-secondary"`}
        >
          <button className="btn-secondary">Cancel</button>
          <button disabled className="btn-secondary">
            Disabled
          </button>
        </Item>
        <Item
          name="Outline / suggestion button"
          description="apply-a-suggestion chips"
          snippet={`className="rounded-lg border border-primary-300 px-3 py-1 text-sm text-primary-700 hover:bg-primary-50 dark:border-primary-700 dark:text-primary-200 dark:hover:bg-primary-950"`}
        >
          <button className="rounded-lg border border-primary-300 px-3 py-1 text-sm text-primary-700 hover:bg-primary-50 dark:border-primary-700 dark:text-primary-200 dark:hover:bg-primary-950">
            + warm
          </button>
        </Item>
        <Item
          name="Pill button"
          description="compact status / nav (e.g. credit balance)"
          snippet={`className="rounded-full bg-gray-100 px-3 py-1 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700"`}
        >
          <span className="rounded-full bg-gray-100 px-3 py-1 text-sm hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700">
            42 credits
          </span>
        </Item>
        <Item
          name="Text button & link"
          description="low-emphasis actions"
          snippet={`// button: className="text-sm text-gray-500 hover:text-gray-700"\n// link:   className="text-sm text-primary-600 hover:underline"`}
        >
          <button className="text-sm text-gray-500 hover:text-gray-700">Sign out</button>
          <a className="text-sm text-primary-600 hover:underline">← Back to editor</a>
        </Item>
      </Section>

      <Section title="Inputs">
        <Item
          name="Text input"
          description="email / password / single-line"
          snippet={`className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-900"`}
        >
          <input
            placeholder="Email"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-900"
          />
        </Item>
        <Item
          name="Mono textarea"
          description="the Style / Genre prompt box"
          snippet={`className="w-full rounded-xl border border-gray-300 p-3 font-mono text-sm dark:border-gray-700 dark:bg-gray-900"`}
        >
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            className="w-full rounded-xl border border-gray-300 p-3 font-mono text-sm dark:border-gray-700 dark:bg-gray-900"
          />
        </Item>
        <Item
          name="Borderless title input"
          description="inline editable headings (song title)"
          snippet={`className="w-full border-b border-transparent bg-transparent text-xl font-semibold focus:border-gray-300 focus:outline-none"`}
        >
          <input
            placeholder="Song title"
            className="w-full border-b border-transparent bg-transparent text-xl font-semibold focus:border-gray-300 focus:outline-none"
          />
        </Item>
        <Item
          name="Section label"
          description="uppercase field captions"
          snippet={`className="text-sm font-semibold uppercase tracking-wide text-gray-500"`}
        >
          <span className="text-sm font-semibold uppercase tracking-wide text-gray-500">Style / Genre</span>
        </Item>
      </Section>

      <Section title="Surfaces & badges">
        <Item
          name="Card"
          description="bordered content container"
          snippet={`className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900"`}
        >
          <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
            <div className="text-lg font-semibold">Plus</div>
            <div className="mt-1 text-2xl font-bold">$9/mo</div>
            <div className="mt-1 text-sm text-gray-500">500 credits / month</div>
          </div>
        </Item>
        <Item
          name="Selected card"
          description="active state — ring + primary border"
          snippet={`className="rounded-xl border border-primary-500 ring-2 ring-primary-300 dark:ring-primary-700 ..."`}
        >
          <div className="rounded-xl border border-primary-500 bg-white p-5 ring-2 ring-primary-300 dark:bg-gray-900 dark:ring-primary-700">
            <div className="font-semibold">The Rapper</div>
            <div className="text-xs uppercase tracking-wide text-primary-500">Hip-Hop</div>
          </div>
        </Item>
        <Item
          name="Badge"
          description="card kind / category tag"
          snippet={`className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900 dark:text-primary-200"`}
        >
          <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900 dark:text-primary-200">
            Replace vague word
          </span>
          <span className="text-xs uppercase tracking-wide text-gray-400">mood</span>
        </Item>
      </Section>

      <Section title="Feedback">
        <Item name="Success banner" description="confirmation messages">
          <p className="rounded-lg bg-green-100 px-4 py-2 text-sm text-green-800 dark:bg-green-900 dark:text-green-200">
            Subscription active — your credits have been topped up.
          </p>
        </Item>
        <Item name="Warning banner" description="cancelled / caution">
          <p className="rounded-lg bg-amber-100 px-4 py-2 text-sm text-amber-800 dark:bg-amber-900 dark:text-amber-200">
            Checkout cancelled.
          </p>
        </Item>
        <Item name="Inline error" description="form / mutation errors">
          <p className="text-sm text-red-500">Could not improve the prompt right now.</p>
        </Item>
        <Item name="Score badge" description="free, rule-based completeness score — click to expand suggestion cards (see <ScoreBadge /> below)">
          <ScoreBadge analysis={SAMPLE_ANALYSIS} open={false} onToggle={() => {}} />
        </Item>
      </Section>

      <Section title="Overlays">
        <Item
          name="Modal / signup overlay"
          description="centered card over a dimmed backdrop (e.g. the anon signup wall)"
          snippet={`<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
  <div className="relative w-full max-w-sm rounded-2xl border border-gray-800 bg-gray-950 p-6">
    …
  </div>
</div>`}
        >
          <div className="flex w-full items-center justify-center rounded-lg bg-black/60 p-6">
            <div className="w-48 rounded-2xl border border-gray-800 bg-gray-950 p-4 text-center text-sm text-gray-400">
              Sign up free
            </div>
          </div>
        </Item>
      </Section>

      <Section title="Components">
        <p className="-mt-2 text-sm text-gray-500">
          Reusable React components in <code className="text-xs">app/javascript/components</code>. Data-driven
          ones (auth/queries required) are described rather than rendered.
        </p>

        <Item name="<ScoreBadge />" description="tiny completeness-score pill for a Style prompt; click expands the breakdown + cards">
          <ScoreBadge analysis={SAMPLE_ANALYSIS} open={false} onToggle={() => {}} />
        </Item>

        <Item name="<StyleField />" description="Style editor: interactive smart card (descriptor chips, vague flags, inline 'missing' placeholders) + static score badge, with a toggle back to a plain textarea">
          <div className="w-full">
            <StyleFieldDemo />
          </div>
        </Item>

        <Item name="<SuggestionCardList />" description="applyable suggestion cards — shared by the free rule layer and the LLM 'Improve' flow">
          <div className="w-full">
            <SuggestionCardList cards={SAMPLE_CARDS} onApply={() => {}} />
          </div>
        </Item>

        <Item name="<ShowcaseGallery />" description="anon on-ramp — example prompts that pre-fill the live Style box">
          <div className="w-full">
            <ShowcaseGallery onUse={() => {}} />
          </div>
        </Item>

        <Item name="<ExtensionCallout />" description="promo strip for the Chrome extension — shown under the Style editor; CTA → CHROME_EXTENSION_URL">
          <div className="w-full">
            <ExtensionCallout />
          </div>
        </Item>

        <ComponentNote
          name="<AuthForm />"
          purpose="Email/password sign in & sign up, with Google button. Shown in the signup overlay when an anonymous visitor hits a wall (4th improve, lyrics, or save)."
          usedIn="StyleEditorPage signup overlay, extension popup"
        />
        <ComponentNote
          name="<GoogleSignInButton />"
          purpose="Renders Google Identity Services button when a client id is configured; exchanges the id_token via googleSignIn."
          usedIn="AuthForm"
        />
        <ComponentNote
          name="<CharacterPicker />"
          purpose="Selectable grid of the lyric-persona roster (fetches characters)."
          usedIn="LyricsPanel"
        />
        <ComponentNote
          name="<LyricsPanel />"
          purpose="Character-voiced lyric generation panel (theme/draft → lyrics + suggested style)."
          usedIn="StyleEditorPage"
        />
        <ComponentNote
          name="<ProjectsBar />"
          purpose="Sidebar listing the current user's saved songs; save / load / new."
          usedIn="StyleEditorPage"
        />
      </Section>
    </div>
  )
}

// Stateful demo for the StyleField editor (it needs a controlled value).
function StyleFieldDemo() {
  const [value, setValue] = useState('nice lo-fi love song')
  return <StyleField value={value} onChange={setValue} analysis={SAMPLE_ANALYSIS} onApply={() => {}} />
}

// A described-only component entry (for components that need auth/network to render).
function ComponentNote({ name, purpose, usedIn }: { name: string; purpose: string; usedIn: string }) {
  return (
    <div className="rounded-xl border border-gray-200 p-5 dark:border-gray-800">
      <h3 className="font-mono text-sm font-semibold text-primary-700 dark:text-primary-300">{name}</h3>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{purpose}</p>
      <p className="mt-1 text-xs text-gray-400">Used in: {usedIn}</p>
    </div>
  )
}
